import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import './AudioPlayer.css';

const AudioPlayer = ({ 
  trackId, 
  trackTitle, 
  artistName, 
  previewStart = 30, 
  previewDuration = 30,
  onPlayStateChange,
  artworkUrl
}) => {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(previewDuration);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const relativeTime = audio.currentTime - previewStart;
      setCurrentTime(Math.max(0, Math.min(relativeTime, previewDuration)));
      
      // Stop at preview end
      if (audio.currentTime >= previewStart + previewDuration) {
        audio.pause();
        audio.currentTime = previewStart;
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(previewDuration);
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [previewStart, previewDuration]);

  useEffect(() => {
    if (onPlayStateChange) {
      onPlayStateChange(isPlaying);
    }
  }, [isPlaying, onPlayStateChange]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      try {
        // Set to preview start position
        audio.currentTime = previewStart;
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.error('Playback error:', err);
        setIsLoading(false);
      }
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !audio.muted;
      setIsMuted(!isMuted);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    const progress = progressRef.current;
    if (!audio || !progress) return;

    const rect = progress.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newTime = pos * previewDuration;
    
    audio.currentTime = previewStart + newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = (currentTime / previewDuration) * 100;

  return (
    <div className="audio-player" data-testid={`audio-player-${trackId}`}>
      <audio
        ref={audioRef}
        src={`${API_URL}/api/store/tracks/${trackId}/preview`}
        preload="none"
      />
      
      <div className="player-controls">
        {artworkUrl && (
          <img src={artworkUrl} alt={trackTitle} className="player-artwork" />
        )}
        
        <button 
          className={`play-btn ${isLoading ? 'loading' : ''}`}
          onClick={togglePlay}
          disabled={isLoading}
          data-testid="play-button"
        >
          {isLoading ? (
            <div className="spinner" />
          ) : isPlaying ? (
            <Pause size={20} fill="currentColor" />
          ) : (
            <Play size={20} fill="currentColor" />
          )}
        </button>

        <div className="player-info">
          <div className="track-title">{trackTitle}</div>
          <div className="track-artist">{artistName}</div>
        </div>

        <div 
          className="progress-bar" 
          ref={progressRef}
          onClick={handleProgressClick}
        >
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="time-display">
          <span>{formatTime(currentTime)}</span>
          <span>/</span>
          <span>{formatTime(previewDuration)}</span>
        </div>

        <button className="mute-btn" onClick={toggleMute}>
          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </div>

      <div className="preview-badge">30s Preview</div>
    </div>
  );
};

export default AudioPlayer;
